import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { SectionTitle } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DateField } from "@/components/common";
import { Plus, FolderKanban } from "lucide-react";
import { toast } from "sonner";
import { OfficeTaskForm } from "@/components/office/OfficeTaskForm";
import { OfficeTaskDetail } from "@/components/office/OfficeTaskDetail";
import { TaskChip } from "./MyWork";

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [detail, setDetail] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", deadline: "", members: [] });
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState(null);
  const [taskFormOpen, setTaskFormOpen] = useState(false);
  const [detailTaskId, setDetailTaskId] = useState(null);

  const load = useCallback(async () => {
    const [p, u, m] = await Promise.all([
      api.get("/office/projects"), api.get("/office/directory").catch(() => { toast.error("Could not load the user directory — check permissions"); return { data: [] }; }), api.get("/office/meta")]);
    setProjects(p.data);
    setUsers(u.data);
    setMeta(m.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (pid) => {
    const r = await api.get(`/office/projects/${pid}`);
    setDetail(r.data);
  };

  const create = async () => {
    if (!form.name.trim()) return toast.error("Project name is required");
    try {
      await api.post("/office/projects", { ...form, deadline: form.deadline || null });
      toast.success("Project created");
      setOpen(false);
      setForm({ name: "", description: "", deadline: "", members: [] });
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Failed");
    }
  };

  const toggleMember = (uid) => setForm((f) => ({ ...f, members: f.members.includes(uid) ? f.members.filter((m) => m !== uid) : [...f.members, uid] }));

  return (
    <div className="space-y-5" data-testid="projects-page">
      <SectionTitle
        right={
          <Dialog open={open} onOpenChange={setOpen}>
            <Button className="bg-primary hover:bg-primary/90 text-primary-foreground" onClick={() => setOpen(true)} data-testid="new-project-button">
              <Plus className="w-4 h-4 mr-1" /> New Project</Button>
            <DialogContent className="bg-card border-border" data-testid="project-dialog">
              <DialogHeader><DialogTitle className="font-heading text-xl uppercase">New Project</DialogTitle>
                <DialogDescription className="sr-only">Create a project with members and deadline</DialogDescription></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs">Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="project-name" /></div>
                <div><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="project-description" /></div>
                <DateField label="Deadline" value={form.deadline} onChange={(v) => setForm({ ...form, deadline: v })} testId="project-deadline" />
                <div>
                  <Label className="text-xs">Members</Label>
                  <div className="grid grid-cols-2 gap-1.5 mt-1 max-h-40 overflow-y-auto">
                    {users.map((u) => (
                      <label key={u.user_id} className="flex items-center gap-2 text-xs border border-border rounded px-2 py-1.5">
                        <input type="checkbox" className="w-3.5 h-3.5 accent-primary" checked={form.members.includes(u.user_id)}
                          onChange={() => toggleMember(u.user_id)} data-testid={`project-member-${u.user_id}`} />
                        {u.name}
                      </label>))}
                  </div>
                </div>
                <Button className="w-full bg-primary text-primary-foreground" onClick={create} data-testid="project-create-submit">Create Project</Button>
              </div>
            </DialogContent>
          </Dialog>}>
        Projects
      </SectionTitle>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {projects.map((p) => (
          <button key={p.project_id} onClick={() => openDetail(p.project_id)}
            className="bg-card border border-border rounded-lg p-4 text-left hover:border-primary/50 transition-colors" data-testid={`project-${p.project_id}`}>
            <div className="flex items-center gap-2">
              <FolderKanban className="w-4 h-4 text-primary" />
              <span className="font-semibold text-sm truncate">{p.name}</span>
            </div>
            <p className="metric text-[10px] text-muted-foreground mt-0.5">{p.project_id}{p.deadline ? ` · due ${p.deadline}` : ""}</p>
            <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-signal-green rounded-full" style={{ width: `${p.progress}%` }} data-testid={`project-progress-${p.project_id}`} />
            </div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mt-1">{p.done_count}/{p.task_count} tasks · {p.progress}%</p>
          </button>))}
        {projects.length === 0 && <p className="text-sm text-muted-foreground">No projects yet — create one for longer multi-person assignments.</p>}
      </div>

      {detail && (
        <div className="bg-card border border-border rounded-lg p-4 space-y-3" data-testid="project-detail">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold uppercase tracking-tight">{detail.name} <span className="metric text-primary text-sm">{detail.project_id}</span></h3>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => setTaskFormOpen(true)} data-testid="project-add-task">Add Task</Button>
              <Button size="sm" variant="ghost" onClick={() => setDetail(null)} data-testid="project-detail-close">Close</Button>
            </div>
          </div>
          {detail.description && <p className="text-sm text-muted-foreground">{detail.description}</p>}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
            {(detail.tasks || []).map((t) => <TaskChip key={t.task_id} t={t} onClick={() => setDetailTaskId(t.task_id)} />)}
            {(detail.tasks || []).length === 0 && <p className="text-xs text-muted-foreground">No tasks yet.</p>}
          </div>
        </div>)}

      {meta && detail && (
        <OfficeTaskForm open={taskFormOpen} onOpenChange={setTaskFormOpen} task={null} users={users} projects={projects}
          meta={meta} fixedProject={detail.project_id} onSaved={() => { openDetail(detail.project_id); load(); }} />)}
      <OfficeTaskDetail taskId={detailTaskId} onClose={() => setDetailTaskId(null)} onChanged={() => { if (detail) openDetail(detail.project_id); load(); }} />
    </div>
  );
}
